<template>
  <div>
    <div class="d-flex align-center" style="min-height: 40px">
      <b>{{ $t('PROJECT_SHEET.USERS') }}</b>
    </div>

    <v-card v-if="projectUsers.length" class="pa-6 d-flex flex-column gap-2">
      <div v-for="userEntry in projectUsers" :key="userEntry.key" class="d-flex align-center gap-2 line-height-normal">
        <div class="mr-1">
          <user-avatar :user="userEntry.user" tooltip size="32"></user-avatar>
        </div>

        <div v-for="statData in statDataItems" :key="statData.id" class="d-flex align-center flex-grow-1" style="flex-basis: 20%">
          <v-icon size="small" class="text-medium-emphasis mr-1" :title="userEntry[statData.id] && $t(statData.countText, countTextParams(statData.id, userEntry[statData.id]))">
            {{ statData.icon }}
          </v-icon>
          <div v-if="userEntry[statData.id].total" class="rounded overflow-hidden d-flex flex-grow-1" style="height: 10px">
            <div v-for="(stat, id) in userEntry[statData.id].items" :key="id" :title="userStatTitle(statData.id, stat)"
                 :class="stat.class" :style="{ width: `${stat.percent}%` }">
            </div>
          </div>
          <div v-else class="text-medium-emphasis small ml-2">-</div>
        </div>
      </div>
      <div class="text-center mt-auto export-hidden">
        <v-btn variant="text" @click="$emit('open-project-report', 'users')">
          {{ $t('PROJECT_SHEET.SEE_ALL_USERS') }}
          <v-icon size="small" class="ml-1">far fa-arrow-right</v-icon>
        </v-btn>
      </div>
    </v-card>
    <v-card v-else class="pa-6 text-center text-medium-emphasis">
      {{ $t('PROJECT_SHEET.NO_USER') }}
      <div class="mt-2 small font-italic export-hidden">{{ $t('PROJECT_SHEET.NO_USER_DESC') }}</div>
    </v-card>
  </div>
</template>

<script>
  import { buildActionStats, buildBubbleStats, buildWorkloadStats } from '@/components/ProjectSheet/activityHelpers';
  import useUsersAccessRightStore from '@/stores/users/accessRight';

  export default {
    props: {
      planning: { type: Object, required: true },
    },
    emits: ['open-project-report'],
    data() {
      return {
        statDataItems: [
          { id: 'actionStats', icon: 'far fa-check-square', countText: 'GLOBAL.ACTIONS_COUNT', noneText: 'PROJECT_SHEET.NO_ACTION' },
          { id: 'workloadStats', icon: 'far fa-weight-hanging', countText: 'helpers.workloadCount', noneText: 'PROJECT_SHEET.NO_WORKLOAD' },
          { id: 'bubbleStats', icon: 'fas fa-long-arrow-right', countText: 'GLOBAL.BUBBLES_COUNT', noneText: 'PROJECT_SHEET.NO_BUBBLE' },
        ],
        usersAccessRightStore: useUsersAccessRightStore(),
      };
    },
    computed: {
      projectUsers() {
        const users = {};
        this.planning.elements.forEach((item) => {
          if (! item.getUsers()) return;
          item.getUsers().forEach((user) => {
            const key = this.getUserKey(user);
            if (key) users[key] = user;
          });
        });
        return Object.entries(users).map(([key, user]) => {
          const userKey = this.getUserKey(user);
          const elements = this.planning.elements.filter(el => !! (el.getUsers() || []).find(item => this.getUserKey(item) == userKey));
          return {
            key,
            user,
            actionStats: buildActionStats(elements),
            workloadStats: buildWorkloadStats(elements),
            bubbleStats: buildBubbleStats(elements),
          };
        }).sort((userA, userB) => {
          const actionDiff = (userB.actionStats?.total || 0) - (userA.actionStats?.total || 0);
          if (actionDiff) return actionDiff;
          const workloadDiff = (userB.workloadStats?.total || 0) - (userA.workloadStats?.total || 0);
          if (workloadDiff) return workloadDiff;
          return (userB.bubbleStats?.total || 0) - (userA.bubbleStats?.total || 0);
        }).slice(0, 6); // keep the 6 highest totals for display
      },
      workloadInHours() {
        return this.usersAccessRightStore.config.workloadInHours;
      },
    },
    methods: {
      getUserKey(user) {
        return user?.id || (user?.group_id && `group${user.group_id}`) || user?.username || user?.email || null;
      },
      countTextParams(statDataId, stat) {
        if (statDataId == 'workloadStats') {
          return { count: Math.round(stat.total), value: stat.total, workloadInHours: this.workloadInHours };
        }
        return { count: Math.round(stat.total) };
      },
      userStatTitle(statDataId, stat) {
        let unit = '';
        if (statDataId == 'workloadStats') unit = this.$t('helpers.workloadShort', { workloadInHours: this.workloadInHours });
        return `${stat.number}${unit} ${this.$t(stat.label)}`;
      },
    },
  };
</script>
